import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, CardMedia, Chip, Grid, IconButton, InputAdornment, Paper, Stack, Table, TableBody, TableCell, TableHead, TableContainer, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material"
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useNavigate } from 'react-router'
import { getSlugsAsync, selectSlugLoading, selectSlugs } from '../slugs/SlugSlice'
import { Link, ArrowUpward, ArrowDownward, Search, Clear, MailOutline, ScheduleOutlined } from '@mui/icons-material'
import { selectAuthUser } from '../auth/AuthSlice'
import { selectDomainCss } from '../analysis/AnalysisSlice'
import { format, parseISO } from 'date-fns'

export interface SlugTableProps {
  mode: 'all' | 'my' | 'myDomain' | 'byEmail' | 'byEmailDomain',
  arg?: string
}

// Full timestamp (date + time). Falls back gracefully if the value isn't parseable.
const formatTimestamp = (iso?: string) => {
  if (!iso) return ''
  try {
    return format(parseISO(iso), 'MMM d, yyyy · h:mm a')
  } catch {
    return iso.substring(0, 16).replace('T', ' ')
  }
}

const SlugTable = (props: SlugTableProps) => {

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const slugs = useAppSelector(selectSlugs)
  const loading = useAppSelector(selectSlugLoading)
  const [filter, setFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [sort, setSort] = useState('createdTime,desc')
  const [size, setSize] = useState(50)
  const [page, setPage] = useState(0)
  const authUser = useAppSelector(selectAuthUser)
  const domainCss = useAppSelector(selectDomainCss)

  // Multi-user views (org / admin / all) benefit from the generating email; a user's own
  // list is all self, so the email column is redundant there.
  const showEmail = props.mode === 'all' || props.mode === 'myDomain' || props.mode === 'byEmailDomain'

  useEffect(() => {
    if (authUser) {
      dispatch(getSlugsAsync({ page: { page, size, sort }, mode: props.mode, arg: props.arg ? props.arg : '' }))
    }
  }, [authUser])

  // Client-side filter over the currently-loaded page: free-text (email or address) + date range.
  const matchesFilter = (slug: any) => {
    const q = filter.trim().toLowerCase()
    const textOk = q.length === 0
      || (slug.userEmail?.toLowerCase().indexOf(q) > -1)
      || (slug.address?.toLowerCase().indexOf(q) > -1)
    const t = slug.createdTime ? new Date(slug.createdTime).getTime() : NaN
    const fromOk = !fromDate || (!isNaN(t) && t >= new Date(fromDate + 'T00:00:00').getTime())
    const toOk = !toDate || (!isNaN(t) && t <= new Date(toDate + 'T23:59:59').getTime())
    return textOk && fromOk && toOk
  }

  const hasActiveFilter = filter.trim().length > 0 || !!fromDate || !!toDate
  const clearFilters = () => { setFilter(''); setFromDate(''); setToDate('') }

  const sortToggle = (field: string) => {
    const tmp = (sort.startsWith(field) && sort.endsWith('desc')) ? `${field},asc` : `${field},desc`
    setSort(tmp)
    dispatch(getSlugsAsync({ page: { page, size, sort: tmp }, mode: props.mode, arg: props.arg ? props.arg : '' }))
  }

  const sortIndicator = (field: string) =>
    sort.startsWith(field) ? (sort === `${field},desc` ? <ArrowDownward fontSize="inherit" sx={{ ml: 0.5 }} /> : <ArrowUpward fontSize="inherit" sx={{ ml: 0.5 }} />) : <></>

  // The card grid (my mode) shows the first 10; the table shows the rest (or everything in other modes).
  const cardSlugs = slugs.content.filter((_slug, index) => index < 10)
  const tableSlugs = slugs.content.filter((slug, index) => matchesFilter(slug) && (index > 9 || props.mode !== 'my'))
  const tableEligibleCount = slugs.content.filter((_slug, index) => index > 9 || props.mode !== 'my').length

  return (slugs && slugs.totalElements && slugs.totalElements > 0) ? <Box>
    {props.mode === 'my' && <Grid container spacing={2}>
      {cardSlugs.map((slug: any, index: number) => <Grid item xs={12} sm={6} md={4} key={index}>
        <Card>
          <CardMedia component="img" image={slug.img && slug.img.length > 10 ? slug.img : '/images/v2/no-image.png'} onError={(e: any) => { e.target.src = '/images/v2/no-image.png' }}
            sx={{ cursor: 'pointer', maxHeight: '250px' }} onClick={() => navigate({ pathname: `/analysis/slug/${slug.slug}` })} />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {slug.address}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: domainCss?.instructionColor }}>
                <ScheduleOutlined sx={{ fontSize: 14 }} />
                <Typography variant="caption">
                  {formatTimestamp(slug.createdTime)}
                </Typography>
              </Box>
              {slug.userEmail && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <MailOutline sx={{ fontSize: 14 }} />
                <Typography variant="caption" noWrap title={slug.userEmail}>
                  {slug.userEmail}
                </Typography>
              </Box>}
            </Box>
          </CardContent>
        </Card>
      </Grid>)}
    </Grid>
    }
    {(slugs.totalElements > 10 || props.mode !== 'my') && <>
      {/* Filter toolbar */}
      <Paper variant="outlined" sx={{ p: 1.5, mt: props.mode === 'my' ? 3 : 0, mb: 1.5, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder={showEmail ? 'Search address or email' : 'Search address'}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 260 }, flexGrow: 1 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
              endAdornment: filter ? <InputAdornment position="end"><IconButton size="small" onClick={() => setFilter('')} aria-label="clear search"><Clear fontSize="small" /></IconButton></InputAdornment> : undefined
            }}
          />
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ width: { xs: '100%', sm: 170 } }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ width: { xs: '100%', sm: 170 } }} />
          {hasActiveFilter && <Button size="small" startIcon={<Clear />} onClick={clearFilters} sx={{ textTransform: 'none' }}>Clear</Button>}
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            size="small"
            variant="outlined"
            label={hasActiveFilter ? `${tableSlugs.length} of ${tableEligibleCount} on this page` : `${tableEligibleCount} on this page`}
            sx={{ color: 'text.secondary' }}
          />
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 650 }} size="small" aria-label="Recent search table">
          <TableHead>
            <TableRow sx={{ '& th': { backgroundColor: 'action.hover', fontWeight: 600 } }}>
              {showEmail && <TableCell>
                <Button disabled={loading} onClick={() => sortToggle('userEmail')} sx={{ textTransform: 'none', color: 'text.primary', minWidth: 0 }}>
                  Email {sortIndicator('userEmail')}
                </Button>
              </TableCell>}
              <TableCell>
                <Button disabled={loading} onClick={() => sortToggle('address')} sx={{ textTransform: 'none', color: 'text.primary', minWidth: 0 }}>
                  Address {sortIndicator('address')}
                </Button>
              </TableCell>
              <TableCell>
                <Button disabled={loading} onClick={() => sortToggle('createdTime')} sx={{ textTransform: 'none', color: 'text.primary', minWidth: 0 }}>
                  Generated {sortIndicator('createdTime')}
                </Button>
              </TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableSlugs.length === 0 ? <TableRow>
              <TableCell colSpan={showEmail ? 4 : 3}>
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
                  No reports on this page match your filter.
                </Typography>
              </TableCell>
            </TableRow> : tableSlugs.map((slug: any, index: number) => <TableRow key={`${slug.id}-${index}`} hover>
              {showEmail && <TableCell sx={{ color: 'text.secondary' }}>
                {slug.userEmail}
              </TableCell>}
              <TableCell sx={{ fontWeight: 500 }}>
                {slug.address}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                <Tooltip title={slug.createdTime || ''}>
                  <span>{formatTimestamp(slug.createdTime)}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" color="primary" disabled={slug.role === 'ROLE_ADMIN'} onClick={() => navigate({ pathname: `/analysis/slug/${slug.slug}` })} aria-label="open report">
                  <Link fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
        <TablePagination
          backIconButtonProps={loading ? { disabled: true } : {}}
          nextIconButtonProps={loading ? { disabled: true } : {}}
          rowsPerPageOptions={[25, 50, 100, 250, 500]}
          component="div"
          count={slugs.totalElements}
          page={slugs.number}
          onPageChange={(event, newPage) => {
            setPage(newPage)
            dispatch(getSlugsAsync({ page: { page: newPage, size, sort }, mode: props.mode, arg: props.arg ? props.arg : '' }))
          }}
          rowsPerPage={slugs.size}
          onRowsPerPageChange={(event) => {
            setSize(parseInt(event.target.value))
            dispatch(getSlugsAsync({ page: { page: 0, size: parseInt(event.target.value), sort }, mode: props.mode, arg: props.arg ? props.arg : '' }))
          }}
        />
      </TableContainer>
    </>}
  </Box> : <></>
}

export default SlugTable
